import React, { useReducer, useRef, useContext, createContext } from 'react';
import { createContainer } from 'react-tracked';

export const ParticipantLiveStateContext = createContext(null);

const DEFAULT_PIN_STATE = { cam: false, share: false };

export const PARTICIPANT_STATE_KEYS = {
  participant: true,
  displayName: true,
  metaData: true,
  micOn: true,
  webcamOn: true,
  screenShareOn: true,
  screenShareAudioOn: true,
  webcamStream: true,
  micStream: true,
  screenShareStream: true,
  screenShareAudioStream: true,
  isLocal: true,
  isActiveSpeaker: true,
  isMainParticipant: true,
  pinState: true,
  mode: true,
  // agent-specific
  agentState: true,
  agentId: true,
  id: true,
  isAgent: true
};

export const EMPTY_PARTICIPANT_STATE = {
  participant: null,
  displayName: undefined,
  metaData: undefined,
  micOn: false,
  webcamOn: false,
  screenShareOn: false,
  screenShareAudioOn: false,
  webcamStream: null,
  micStream: null,
  screenShareStream: null,
  screenShareAudioStream: null,
  isLocal: false,
  isActiveSpeaker: false,
  isMainParticipant: false,
  pinState: DEFAULT_PIN_STATE,
  mode: undefined,
  agentState: null,
  agentId: undefined,
  id: undefined,
  isAgent: false
};

// State shape: { [participantId]: ParticipantData }
const reducer = (state, action) => {
  if (action === 'RESET') return {};

  const { type, id, data } = action;

  switch (type) {
    case 'INIT': {
      return {
        ...state,
        [id]: { ...EMPTY_PARTICIPANT_STATE, ...data }
      };
    }

    case 'REMOVE': {
      const { [id]: _, ...rest } = state;
      return rest;
    }

    case 'UPDATE': {
      const prev = state[id];
      if (!prev) return state;

      // Bail out if nothing actually changed
      const hasChange = Object.keys(data).some(k => prev[k] !== data[k]);
      if (!hasChange) return state;

      return { ...state, [id]: { ...prev, ...data } };
    }

    default:
      return state;
  }
};

const {
  Provider: ParticipantStateProviderBase,
  useTrackedState: useParticipantTrackedState,
  useSelector: useParticipantSelector,
  useUpdate: useParticipantDispatch
} = createContainer(() => useReducer(reducer, {}));

export const ParticipantStateProvider = ({ children }) => (
  <ParticipantStateProviderBase>{children}</ParticipantStateProviderBase>
);

export {
  useParticipantTrackedState,
  useParticipantSelector,
  useParticipantDispatch
};
